import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  archiveTargetPeriod as archiveTargetPeriodApi,
  createTargetPeriod,
  deleteTargetPeriod as deleteTargetPeriodApi,
  listBaselineYears,
  listTargetPeriods,
  updateTargetPeriod,
} from '../../../api/climateTargetApi';
import { getYearlyEmissionOverview } from '../../../api/emissionsApi';
import {
  baselineYearsToInventories,
  serializeTargetPeriodDraft,
  targetPeriodsToCycles,
} from '../components/targetPeriodModel';

const isCanceled = error => (
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'
);

/** Load target periods, baseline years, and scope inventories from the backend. */
export default function useClimateTargetMaster() {
  const [targetPeriods, setTargetPeriods] = useState([]);
  const [baselineYears, setBaselineYears] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadSequence, setReloadSequence] = useState(0);
  const sequenceRef = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const sequence = sequenceRef.current + 1;
    sequenceRef.current = sequence;
    setLoading(true);
    setError(null);

    const loadTargetPeriods = listTargetPeriods({ signal: controller.signal })
      .then((payload) => {
        if (sequenceRef.current !== sequence) return;
        setTargetPeriods(Array.isArray(payload?.target_periods)
          ? payload.target_periods : []);
      })
      .catch((loadError) => {
        if (sequenceRef.current === sequence && !isCanceled(loadError)) {
          setError(loadError);
        }
      });

    const loadBaselineYears = listBaselineYears({ signal: controller.signal })
      .then((payload) => {
        if (sequenceRef.current !== sequence) return;
        setBaselineYears(Array.isArray(payload?.fiscal_years)
          ? payload.fiscal_years.map(Number) : []);
      })
      .catch(() => { /* baseline years are advisory; never block the list */ });

    const loadInventories = getYearlyEmissionOverview({ signal: controller.signal })
      .then((payload) => {
        if (sequenceRef.current !== sequence) return;
        setInventories(baselineYearsToInventories(payload?.years || []));
      })
      .catch(() => { /* inventories feed the dialog preview only; never block the list */ });

    Promise.all([loadTargetPeriods, loadBaselineYears, loadInventories])
      .finally(() => {
        if (sequenceRef.current === sequence) setLoading(false);
      });

    return () => controller.abort();
  }, [reloadSequence]);

  const reload = useCallback(() => {
    setReloadSequence(sequence => sequence + 1);
  }, []);

  const targetCycles = useMemo(() => targetPeriodsToCycles(targetPeriods), [targetPeriods]);

  const targets = useMemo(() => {
    const activeCycle = targetCycles.find(cycle => cycle.status === 'ACTIVE');
    return activeCycle?.targets || {};
  }, [targetCycles]);

  const saveTargetPeriod = useCallback(async (targetBatch, periodId = null) => {
    const payload = serializeTargetPeriodDraft(targetBatch);
    if (targetBatch?.createNewPeriod) {
      await createTargetPeriod(payload);
    } else {
      await updateTargetPeriod(periodId, payload);
    }
    setReloadSequence(sequence => sequence + 1);
  }, []);

  const archiveTargetPeriod = useCallback(async (periodId) => {
    await archiveTargetPeriodApi(periodId);
    setReloadSequence(sequence => sequence + 1);
  }, []);

  const deleteTargetPeriod = useCallback(async (periodId) => {
    await deleteTargetPeriodApi(periodId);
    setReloadSequence(sequence => sequence + 1);
  }, []);

  return {
    targetCycles,
    targets,
    baselineYears,
    inventories,
    loading,
    error,
    reload,
    saveTargetPeriod,
    archiveTargetPeriod,
    deleteTargetPeriod,
  };
}